import type { RouteListItemDto } from '@artic/api-client';
import { Badge } from '@artic/ui/badge';
import { useFeatureFlag } from '@artic/ui/feature-flags';
import { FullHeightChrome, FullHeightStack } from '@artic/ui/full-height-layout';
import { Skeleton } from '@artic/ui/skeleton';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryState } from 'nuqs';
import { useMemo } from 'react';

import { customColumnDefinitionsQueryOptions } from '../../features/custom-columns/queries';
import { usePartnerId } from '../../features/inbox/use-partner-name';
import { partnerContainersQueryOptions } from '../../features/products/queries';
import { factoriesQueryOptions } from '../../features/project-planning/gantt/queries';
import { ProjectGridSkeleton } from '../../features/project-planning/project-grid-skeleton';
import { ProjectPlanningEditor } from '../../features/project-planning/project-planning-editor';
import {
  projectBreadcrumbBadges,
  projectHeaderActions,
} from '../../features/project-planning/project-route-chrome';
import { ProjectStepper } from '../../features/project-planning/project-stepper';
import {
  projectArchivedWorkOrdersQueryOptions,
  projectLaborShiftSummaryQueryOptions,
  projectPlanningDetailQueryOptions,
} from '../../features/project-planning/queries';
import { RunSheetsRenameTour } from '../../features/project-planning/run-sheets-rename-tour';
import { projectEditorSearchParsers } from '../../features/project-planning/search';
import { useProjectChannel } from '../../features/realtime/use-project-channel';
import { routesListQueryOptions } from '../../features/routing/queries';
import { partnerStructuredColumnsQueryOptions } from '../../features/structured-columns/queries';

// Stable fallback for the routes prop. An inline `?? []` would mint a new
// array identity on every render while the routing query is disabled or
// pending, and `routes` is a dependency of the editor's columnDefs memo — so
// each render would rebuild the column definitions and clobber user column
// state (pins, widths, order).
const EMPTY_ROUTES: RouteListItemDto[] = [];

export const Route = createFileRoute('/projects/$projectId')({
  staticData: {
    breadcrumb: (match, queryClient) => {
      const { projectId } = match.params as { projectId: string };
      const data = queryClient.getQueryData(projectPlanningDetailQueryOptions(projectId).queryKey);
      return data?.project_name ?? null;
    },
    // ENG-720 Band 1: state chip + Update Project sit inline with the
    // breadcrumb, and the Grid/Timeline toggle pins to the header's trailing
    // edge — replacing the old page-level ProjectPageHeader bar.
    breadcrumbBadges: projectBreadcrumbBadges,
    headerActions: projectHeaderActions,
    layout: 'full-height',
  },
  loader: async ({ context: { queryClient }, params: { projectId } }) => {
    // Deliberately NOT awaited. Awaiting would serialise detail → editor mount
    // → the partner-scoped wave (factories / custom + structured columns), the
    // exact serial-fetch stall we're removing. `prefetchQuery` swallows the
    // rejection, so a load error surfaces through the component's `useQuery`
    // error branch (not the router). With defaultPreload: 'intent' this warms
    // the cache on hover, before the component ever mounts.
    void queryClient.prefetchQuery(projectPlanningDetailQueryOptions(projectId));
    await queryClient.ensureQueryData(projectLaborShiftSummaryQueryOptions(projectId));
  },
  component: ProjectPlanningContent,
});

function ProjectPlanningContent() {
  const { projectId } = Route.useParams();
  const activePartnerId = usePartnerId();

  // Realtime presence + live work-order updates. Joining by the route param
  // keeps the channel topic in lockstep with the query cache key the editor
  // invalidates on inbound updates.
  const { viewers, channel: realtimeChannel } = useProjectChannel(projectId, {
    enabled: true,
  });
  // Shared with the toolbar's "Show archived" toggle (same URL param) — when
  // on, archived work orders are fetched separately and merged in for display
  // so they can be selected and unarchived.
  const [showArchived] = useQueryState('show_archived', projectEditorSearchParsers.show_archived);

  // One parallel wave: the project detail plus every partner-scoped,
  // column-defining query fire together at first render. The active partner
  // starts that wave; the project's own partner_id replaces it below if a
  // multi-partner user opened a project outside their current cookie context.
  const {
    data: project,
    isPending: projectPending,
    isPlaceholderData: projectPlaceholder,
    isError: projectError,
  } = useQuery(projectPlanningDetailQueryOptions(projectId));

  const { data: laborShiftSummary } = useSuspenseQuery(
    projectLaborShiftSummaryQueryOptions(projectId),
  );

  // Start the configuration wave immediately from the active partner in the
  // common case, but let the loaded project become authoritative. A user can
  // legitimately open a bookmarked project from another partner they belong
  // to, so the active cookie is only a speculative first-render key.
  const configurationPartnerId = project?.partner_id ?? activePartnerId;

  const { data: archivedWorkOrders = [] } = useQuery(
    projectArchivedWorkOrdersQueryOptions(projectId, showArchived),
  );

  const {
    data: containers,
    isPending: containersPending,
    isPlaceholderData: containersPlaceholder,
    isError: containersError,
  } = useQuery(partnerContainersQueryOptions(configurationPartnerId));

  const {
    data: factories,
    isPending: factoriesPending,
    isPlaceholderData: factoriesPlaceholder,
    isError: factoriesError,
  } = useQuery(factoriesQueryOptions(configurationPartnerId));

  const {
    data: metadataDefinitions,
    isPending: metadataPending,
    isPlaceholderData: metadataPlaceholder,
    isError: metadataError,
  } = useQuery(customColumnDefinitionsQueryOptions(configurationPartnerId));

  const {
    data: structuredColumns,
    isPending: structuredPending,
    isPlaceholderData: structuredPlaceholder,
    isError: structuredError,
  } = useQuery(partnerStructuredColumnsQueryOptions(configurationPartnerId));

  // ENG-743 §9.1: routes feed the Factory picker's default-route eligibility
  // filter. Flag-off partners never fetch; NON-BLOCKING (not part of
  // gridReady) — while loading, the picker degrades to unfiltered.
  const routingEnabled = useFeatureFlag('routing');
  const { data: routes } = useQuery(
    routesListQueryOptions(routingEnabled ? configurationPartnerId : undefined),
  );

  // Merge archived rows in only when the toggle is on. Returns the base
  // `project` identity unchanged otherwise, so the editor's row memoisation
  // stays stable during normal (non-archived) editing.
  const projectWithArchived = useMemo(() => {
    if (!project) return undefined;
    if (!showArchived || archivedWorkOrders.length === 0) return project;
    return { ...project, work_orders: [...project.work_orders, ...archivedWorkOrders] };
  }, [project, showArchived, archivedWorkOrders]);

  // Tier 1 — no project detail yet: full-page scaffold (a stepper-shaped
  // placeholder in chrome, then the band-shaped grid skeleton).
  if (projectPending || projectPlaceholder) {
    return (
      <FullHeightStack>
        <FullHeightChrome>
          <Skeleton className="h-16 w-full" />
        </FullHeightChrome>
        <ProjectGridSkeleton />
      </FullHeightStack>
    );
  }

  // Any of the five queries erroring → the shared error state.
  if (projectError || containersError || factoriesError || metadataError || structuredError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Error Loading Project</h2>
        <p className="text-muted-foreground text-sm">
          Could not load the project or its configuration. Please try again.
        </p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <Badge variant="outline">Project not found</Badge>
      </div>
    );
  }

  const isProjectRedrafting = project.project_status === 'redrafting';

  // Tier 2 — detail loaded: stepper + header render immediately. The grid slot
  // mounts the editor only once every column-defining query is ready. Gate on
  // `isPending` (not `isLoading`) so same-key refetches keep the grid on
  // screen. Placeholder data is rejected here because it belongs to the
  // previous partner key and must never define this project's columns.
  const gridReady =
    !containersPending &&
    !factoriesPending &&
    !metadataPending &&
    !structuredPending &&
    !containersPlaceholder &&
    !factoriesPlaceholder &&
    !metadataPlaceholder &&
    !structuredPlaceholder;

  return (
    <>
      <FullHeightStack>
        <FullHeightChrome>
          <ProjectStepper
            projectId={project.project_id}
            active="work_orders"
            projectStatus={project.project_status}
            isProjectRedrafting={isProjectRedrafting}
          />
        </FullHeightChrome>
        {gridReady && containers && factories && metadataDefinitions && structuredColumns ? (
          <ProjectPlanningEditor
            project={projectWithArchived ?? project}
            containers={containers}
            factories={factories}
            routes={routes ?? EMPTY_ROUTES}
            metadataDefinitions={metadataDefinitions}
            structuredColumns={structuredColumns}
            realtimeChannel={realtimeChannel}
            viewers={viewers}
            laborShiftSummary={laborShiftSummary}
          />
        ) : (
          <ProjectGridSkeleton />
        )}
      </FullHeightStack>
      <RunSheetsRenameTour />
    </>
  );
}
